import {
  DeleteOutlined,
  InfoCircleOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  List,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { useGetAllPotentialAudienceFilteredQuery } from '../../services/map/potentialAudience';
import { PotentialAudienceProps } from '../../services/map/potentialAudience/potentialAudience';
import * as S from './styles';

const { Option } = Select;
const { Text } = Typography;

interface PotentialAudienceExclusionSimpleProps {
  value?: string[]; // Array de IDs dos públicos excluídos
  onChange?: (value: string[]) => void;
  currentPotentialAudienceId?: string;
  disabled?: boolean;
  customerType?: string; // NOVO: tipo selecionado (PF ou PJ)
}

const PotentialAudienceExclusionSimple: React.FC<
  PotentialAudienceExclusionSimpleProps
> = ({
  value = [],
  onChange,
  currentPotentialAudienceId,
  disabled = false,
  customerType, // NOVO
}) => {
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Buscar todos os PotentialAudience disponíveis
  const { data, isLoading } = useGetAllPotentialAudienceFilteredQuery({
    top: 100, // Limite máximo permitido pelo servidor
    count: true,
  });

  const potentialAudiences = data?.data || [];

  // NOVO: filtrar pelo tipo
  const filteredPotentialAudiences = React.useMemo(() => {
    if (!potentialAudiences) return [];
    if (!customerType) return potentialAudiences;
    return potentialAudiences.filter(pa => pa.customerType === customerType);
  }, [potentialAudiences, customerType]);

  // Filtrar PotentialAudience disponíveis (excluir o atual e os já selecionados)
  const availablePotentialAudiences = React.useMemo(() => {
    if (!filteredPotentialAudiences) return [];
    const excludedIds = new Set([currentPotentialAudienceId, ...value]);
    return filteredPotentialAudiences.filter(pa => !excludedIds.has(pa.id));
  }, [filteredPotentialAudiences, currentPotentialAudienceId, value]);

  const searchFilteredAudiences = useMemo(() => {
    if (!searchTerm.trim()) return availablePotentialAudiences;

    const searchLower = searchTerm.toLowerCase();
    return availablePotentialAudiences.filter(pa =>
      pa.name.toLowerCase().includes(searchLower),
    );
  }, [availablePotentialAudiences, searchTerm]);

  // Obter detalhes dos públicos selecionados
  const selectedPotentialAudiences = useMemo(() => {
    if (!potentialAudiences || !value.length) return [];

    return value
      .map(id => potentialAudiences.find(pa => pa.id === id))
      .filter((pa): pa is PotentialAudienceProps => pa !== undefined)
      .map(pa => ({
        id: pa.id,
        name: pa.name,
        estimatedContactsCount: pa.estimatedContactsCount || 0,
      }));
  }, [potentialAudiences, value]);

  const handleAddExclusion = useCallback(
    (selectedIds: string[]) => {
      onChange?.(selectedIds);
    },
    [onChange],
  );

  const handleRemoveExclusion = useCallback(
    (idToRemove: string) => {
      const newValue = value.filter(id => id !== idToRemove);
      onChange?.(newValue);
    },
    [value, onChange],
  );

  const getTotalExcludedContacts = useMemo(() => {
    return selectedPotentialAudiences.reduce(
      (total, pa) => total + pa.estimatedContactsCount,
      0,
    );
  }, [selectedPotentialAudiences]);

  const handleSearch = useCallback((searchValue: string) => {
    setSearchTerm(searchValue);
  }, []);

  // NOVO: Função para determinar o conteúdo quando não há resultados
  const getNotFoundContent = useCallback(() => {
    if (searchTerm.trim() && searchFilteredAudiences.length === 0) {
      return `Nenhum público encontrado para "${searchTerm}"`;
    }
    if (isLoading) {
      return 'Carregando...';
    }
    return 'Nenhum público disponível';
  }, [searchTerm, searchFilteredAudiences.length, isLoading]);

  return (
    <Card
      title={
        <Space>
          <UserDeleteOutlined />
          <span>Expurgar por Outros Públicos</span>
          <Tooltip title="Selecione seus outros públicos potenciais salvos para expurgar da busca atual">
            <InfoCircleOutlined />
          </Tooltip>
        </Space>
      }
      size="small"
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Seletor múltiplo para adicionar exclusões */}
        <S.SelectionSection>
          <Select
            mode="multiple"
            placeholder="Selecione públicos para expurgar da busca"
            value={value}
            onChange={handleAddExclusion}
            loading={isLoading}
            disabled={disabled}
            style={{ width: '100%' }}
            showSearch
            allowClear
            maxTagCount="responsive"
            onSearch={handleSearch}
            filterOption={false} // Desabilita filtro padrão do Antd para usar nossa busca local
            notFoundContent={getNotFoundContent()}
          >
            {searchFilteredAudiences.map(pa => (
              <Option key={pa.id} value={pa.id}>
                <Space>
                  <span>{pa.name}</span>
                  <Text type="secondary">
                    ({(pa.estimatedContactsCount || 0).toLocaleString()}{' '}
                    contatos)
                  </Text>
                </Space>
              </Option>
            ))}
          </Select>
        </S.SelectionSection>

        {/* Lista de exclusões selecionadas */}
        {selectedPotentialAudiences.length === 0 ? null : (
          <S.SelectedList>
            <List
              size="small"
              dataSource={selectedPotentialAudiences}
              renderItem={item => (
                <List.Item
                  actions={[
                    <Button
                      key="remove"
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveExclusion(item.id)}
                      disabled={disabled}
                    >
                      Remover
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space>
                        <strong>{item.name}</strong>
                        <Tag color="red">Expurgar</Tag>
                      </Space>
                    }
                    description={
                      <Text type="secondary">
                        {item.estimatedContactsCount.toLocaleString()} contatos
                        serão expurgados
                      </Text>
                    }
                  />
                </List.Item>
              )}
            />
          </S.SelectedList>
        )}

        {/* Resumo das exclusões */}
        {selectedPotentialAudiences.length > 0 && (
          <S.SummarySection>
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong> Resumo dados para expurgo:</Text>
              <Space>
                <Text type="secondary">
                  • {selectedPotentialAudiences.length}
                  {selectedPotentialAudiences.length > 1
                    ? ` públicos serão	`
                    : ` público será`}
                  expurgados
                </Text>
                <Text type="secondary">
                  • {getTotalExcludedContacts.toLocaleString()} contatos
                  expurgados da busca
                </Text>
              </Space>
            </Space>
          </S.SummarySection>
        )}
      </Space>
    </Card>
  );
};

export default PotentialAudienceExclusionSimple;
